import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { PaginationState } from '@tanstack/react-table';

interface UseUrlPaginationOptions {
    defaultPageSize?: number;
}

interface UseUrlPaginationResult {
    pagination: PaginationState;
    setPagination: (pagination: PaginationState | ((prev: PaginationState) => PaginationState)) => void;
    resetPagination: () => void;
}

/**
 * Hook for syncing pagination state with URL query parameters.
 */
const useUrlPagination = ({ defaultPageSize = 25 }: UseUrlPaginationOptions = {}): UseUrlPaginationResult => {
    const pageParamName = 'page';
    const pageSizeParamName = 'pageSize';
    const [searchParams, setSearchParams] = useSearchParams();

    // Parse pagination from URL, fallback to defaults
    const pagination = useMemo<PaginationState>(() => {
        const pageParam = searchParams.get(pageParamName);
        const pageSizeParam = searchParams.get(pageSizeParamName);

        const pageIndex = pageParam ? Math.max(0, parseInt(pageParam, 10) - 1) : 0;
        const pageSize = pageSizeParam ? parseInt(pageSizeParam, 10) : defaultPageSize;

        return {
            pageIndex: isNaN(pageIndex) ? 0 : pageIndex,
            pageSize: isNaN(pageSize) ? defaultPageSize : pageSize,
        };
    }, [searchParams, defaultPageSize]);

    // Update URL params when pagination changes
    const setPagination = useCallback(
        (newPagination: PaginationState | ((prev: PaginationState) => PaginationState)) => {
            setSearchParams(
                prevParams => {
                    const params = new URLSearchParams(prevParams);

                    // Get current pagination
                    const pageParam = params.get(pageParamName);
                    const pageSizeParam = params.get(pageSizeParamName);
                    const currentPageIndex = pageParam ? Math.max(0, parseInt(pageParam, 10) - 1) : 0;
                    const currentPageSize = pageSizeParam ? parseInt(pageSizeParam, 10) : defaultPageSize;

                    const current: PaginationState = {
                        pageIndex: isNaN(currentPageIndex) ? 0 : currentPageIndex,
                        pageSize: isNaN(currentPageSize) ? defaultPageSize : currentPageSize,
                    };

                    // Resolve pagination: call if function or use directly if object
                    const resolved = typeof newPagination === 'function' ? newPagination(current) : newPagination;

                    // Set page param (1-based for URL, convert from 0-based pageIndex)
                    const urlPage = resolved.pageIndex + 1;

                    if (urlPage === 1) {
                        params.delete(pageParamName);
                    } else {
                        params.set(pageParamName, urlPage.toString());
                    }

                    // Set pageSize param (only if different from default)
                    if (resolved.pageSize === defaultPageSize) {
                        params.delete(pageSizeParamName);
                    } else {
                        params.set(pageSizeParamName, resolved.pageSize.toString());
                    }

                    return params;
                },
                { replace: true }
            );
        },
        [setSearchParams, defaultPageSize]
    );

    // Reset pagination to defaults (page 1, default pageSize)
    const resetPagination = useCallback(() => {
        setSearchParams(
            prevParams => {
                const params = new URLSearchParams(prevParams);

                params.delete(pageParamName);
                params.delete(pageSizeParamName);

                return params;
            },
            { replace: true }
        );
    }, [setSearchParams]);

    return {
        pagination,
        setPagination,
        resetPagination,
    };
};

export default useUrlPagination;
